import {
	ATTRIBUTES,
	getAttributeOrThrow,
	getAttributeOrUndefined,
	openWaitlistModal,
	PUB_SUB_EVENTS,
	SELECTORS,
} from '@/scripts/core/global'
import { qsRequired } from '@/scripts/core/global'
import { publish } from '@/scripts/core/global'
import { UcoastEl } from '@/scripts/core/UcoastEl'
import {
	addItemsToCart,
	getDOMCartSectionApiIds,
	hasDomCart,
	renderResponseToCartDrawer,
} from '@/scripts/core/cart-functions'
import { CartErrorResponse, ProductVariant } from '@/scripts/shopify'

export class ProductForm extends UcoastEl {
	static htmlSelector = 'product-form'
	static selectors = {
		form: 'form',
		formIdEl: '[name=id]',
		submitButton: '[type="submit"]',
		submitButtonMessage: '[type="submit"] span',
		soldOutMessage: '[data-uc-sold-out-message]',
		errorMessageWrapper: '[data-uc-product-form-error-message-wrapper]',
		errorMessage: '[data-uc-product-form-error-message]',
	}

	url: string
	form: HTMLFormElement
	formIdEl: HTMLInputElement
	submitButton: HTMLButtonElement
	errorMessageWrapper?: HTMLElement
	errorMessage?: HTMLElement
	hideErrors: boolean
	error?: boolean

	constructor() {
		super()

		this.url = getAttributeOrThrow('data-product-url', this)
		this.form = qsRequired(ProductForm.selectors.form, this)
		this.formIdEl = qsRequired(ProductForm.selectors.formIdEl, this.form)
		this.formIdEl.disabled = false
		this.form.addEventListener('submit', this.onSubmitHandler.bind(this))

		this.submitButton = qsRequired(ProductForm.selectors.submitButton, this)
		if (hasDomCart()) {
			this.submitButton.setAttribute('aria-haspopup', 'dialog')
		}

		this.hideErrors = this.dataset.hideErrors === 'true'
	}

	getSpinner() {
		return qsRequired(SELECTORS.loadingOverlaySpinner, this)
	}

	onSubmitHandler(event: Event) {
		event.preventDefault()
		if (this.submitButton.getAttribute('aria-disabled') === 'true') return
		const oosId = getAttributeOrUndefined(
			'data-oos-popup-trigger',
			this.submitButton
		)
		if (oosId) {
			openWaitlistModal(this.url, parseInt(oosId), this.submitButton)
			return
		}
		this.handleErrorMessage()

		this.submitButton.setAttribute('aria-disabled', 'true')
		this.submitButton.setAttribute(ATTRIBUTES.loading, '')
		this.getSpinner().classList.remove('hidden')

		const formData = new FormData(this.form)
		let formVariantId = formData.get('id')
		if (!formVariantId) throw Error('No variant id found')
		const addedVariantId = parseInt(formVariantId.toString())

		addItemsToCart(formData, getDOMCartSectionApiIds())
			.then((cart) => {
				if ('status' in cart) {
					this.handleError(cart, addedVariantId)
					return
				}

				publish(PUB_SUB_EVENTS.cartUpdate, {
					source: 'product-form',
					productVariantId: addedVariantId,
				})
				/*const quickAddModal = closestOptional<QuickAddModal>(
					this,
					'quick-add-modal'
				)*/
				const modal = undefined // TODO: incorporate any related modals here
				renderResponseToCartDrawer(cart, modal)
				this.handleLearnqTracking(addedVariantId, formData.get('quantity'))
			})
			.catch((e) => {
				console.error(e)
			})
			.finally(() => {
				this.submitButton.removeAttribute(ATTRIBUTES.loading)

				if (!this.error)
					this.submitButton.removeAttribute('aria-disabled')
				this.getSpinner().classList.add('hidden')
			})
	}

	handleError(cart: CartErrorResponse, addedVariantId: number) {
		publish(PUB_SUB_EVENTS.cartError, {
			source: 'product-form',
			productVariantId: addedVariantId,
			message: cart.message,
			errors: cart.errors ?? cart.description,
		})
		this.handleErrorMessage(cart.description)

		const soldOutMessage = this.submitButton.querySelector(
			ProductForm.selectors.soldOutMessage
		)
		if (!soldOutMessage) return
		this.submitButton.setAttribute('aria-disabled', 'true')
		const submitButtonText = qsRequired(
			ProductForm.selectors.submitButtonMessage,
			this.submitButton
		)
		submitButtonText.classList.add('hidden')
		soldOutMessage.classList.remove('hidden')
	}

	handleErrorMessage(errorMessage?: string) {
		if (this.hideErrors) return

		this.errorMessageWrapper =
			this.errorMessageWrapper ||
			qsRequired(ProductForm.selectors.errorMessageWrapper, this)
		if (!this.errorMessageWrapper)
			throw new Error('No error message wrapper found')
		this.errorMessage =
			this.errorMessage ||
			qsRequired(
				ProductForm.selectors.errorMessage,
				this.errorMessageWrapper
			)

		this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage)

		if (errorMessage) {
			this.errorMessage.textContent = errorMessage
		}
	}

	getVariantData(): ProductVariant[] {
		const textContent = qsRequired<HTMLScriptElement>(
			'[type="application/json"]',
			this
		).textContent
		if (!textContent) throw new Error('Variant data not found')
		return JSON.parse(textContent)
	}

	handleLearnqTracking(variantId: number, quantity: FormDataEntryValue | null) {
		if (!window.klaviyo) {
			window.klaviyo = []
		}
		const variant = this.getVariantData().find((v) => v.id === variantId)
		if (!variant) return
		const data = {
			id: getAttributeOrThrow('data-product-id', this),
			name: getAttributeOrUndefined('data-tracking-name', this),
			image: getAttributeOrUndefined('data-tracking-image', this),
			url: getAttributeOrUndefined('data-tracking-url', this),
			brand: getAttributeOrUndefined('data-tracking-brand', this),
			currencyISO: getAttributeOrUndefined('data-tracking-currency-iso', this),
			currencySymbol: getAttributeOrUndefined('data-tracking-currency-symbol', this),
			collection: getAttributeOrUndefined(
				'data-tracking-collections',
				this
			)?.split('___'),
		}
		const formData = new FormData(this.form)
		const formVariantId = formData.get('id')
		if (!formVariantId) return
		const trackingQuantity = (typeof quantity === 'string') ? parseInt(quantity) : 1
		const item = {
			ProductID: parseInt(data.id),
			ProductTitle: data.name,
			Price: `${data.currencySymbol}${(variant.price / 100).toFixed(2)} ${data.currencyISO}`,
			CompareAtPrice: `${data.currencySymbol}${(variant.compare_at_price / 100).toFixed(2)} ${data.currencyISO}`,
			URL: data.url,
			ImageURL: data.image,
			Categories: data.collection,
			Brand: data.brand
		}

		window.klaviyo.push([
			'track',
			'Added to Cart',
			item,
		])
	}
}

